import React, { FC, useState, useEffect, useContext } from 'react';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import FillableFieldSelector from './FillableFieldSelector';

import { minimumLoading } from '../../../utils/minimumLoading';

import { FillablePdfField } from '../../../sharedModules/models';
import { WaterAuthority } from '../../model';

import { SelectOptionWithCategory } from '../../../sharedModules/form/model';
import axios from '../../../sharedModules/utils/axios.utils';

interface WaterAuthorityFieldDesignationsProps {
  waterAuthority: WaterAuthority;
  fetchWaterAuthority: () => void;
}

const WaterAuthorityFieldDesignations: FC<WaterAuthorityFieldDesignationsProps> = (props) => {
  const { waterAuthority, fetchWaterAuthority } = props;
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [designationOptions, setDesignationOptions] = useState<SelectOptionWithCategory[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [fillableFields, setFillableFields] = useState<FillablePdfField[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchDesignationOptions = async () => {
    try {
      const response: any = await axios.get('backflows/water_authority_pdf_templates/new', {
        params: {
          water_authority_id: waterAuthority?.id,
        },
      });
      setDesignationOptions(response.data.result);
    } catch (error) {
      setErrorMessage('Something went wrong');
    }
  };

  const fetchFillableFields = async () => {
    try {
      const response: any = await axios.get('/fillable_pdf_fields', {
        params: { organization_type: 'WaterAuthority', organization_id: waterAuthority?.id },
      });

      setFillableFields(response.data.result);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong');
    }
  };

  const handleGenerateSampleDataDemo = async () => {
    setLoading(true);
    try {
      await axios.put(`/backflows/water_authority_pdf_templates/${waterAuthority?.id}`);
      fetchWaterAuthority();
      setErrorMessage('');
      showSuccessSnackBar('Sample Data Ready For Review');
    } catch (error) {
      setErrorMessage('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (waterAuthority?.id) {
      void minimumLoading(async () => {
        await Promise.all([fetchDesignationOptions(), fetchFillableFields()]);
      }, setLoading);
    }
  }, [waterAuthority]);

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage} />
        </Grid>
        {fillableFields.length === 0 ? (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              No fillable fields detected. Please upload a PDF template with fillable form fields.
            </Typography>
          </Grid>
        ) : (
          fillableFields.map((field) => (
            <FillableFieldSelector
              key={field.field_name}
              field={field}
              designationOptions={designationOptions}
              afterUpdate={fetchFillableFields}
            />
          ))
        )}
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleGenerateSampleDataDemo}>
            Generate Sample Data Demo
          </Button>
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage} />
        </Grid>
        {waterAuthority?.sample_data_demo_url && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Demo with Sample Data: </strong>
              <a
                href={waterAuthority.sample_data_demo_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
              >
                View Sample Data →
              </a>
            </Typography>
          </Grid>
        )}
      </Grid>
    </LoadingWrapper>
  );
};

export default WaterAuthorityFieldDesignations;
